import styled, { css } from 'styled-components';
import { Icon as IconComponent } from 'src/components';
import { rtlFlip } from 'src/utils/theme';

export const HeaderSection = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    color: ${theme.co.secondary20};
    padding: ${theme.sz.s4} 0 ${theme.sz.s6} ${theme.sz.s6};
    margin-bottom: ${theme.sz.s5};
  `,
);

export const SidebarWrapper = styled.div`
  height: 100dvh;
  position: relative;
`;

export const Sidebar = styled.div<{ $collapsed?: boolean }>(
  ({ theme, $collapsed }) => css`
    background: ${theme.co.neutral99};
    border-right: ${theme.bo.thinWidth} solid ${theme.co.neutral95};
    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
    width: ${$collapsed ? theme.sz.s28 : theme.sz.s64};
    padding-top: ${theme.sz.s2};
    height: 100%;
    box-sizing: border-box;

    @media (max-width: ${theme.sz.maxMobileBreakpoint}) {
      display: none;
    }
  `,
);

export const SidebarButtonWrapper = styled.div<{ $active?: boolean }>(
  ({ theme, $active }) => css`
    position: relative;
    width: calc(100% - ${theme.sz.s3});
    height: calc(${theme.sz.s16} + 3px); // offset focus-visible border
    display: flex;
    margin-left: ${theme.sz.s3};
    background-color: ${$active ? theme.co.neutral98 : theme.co.neutral99};
  `,
);

export const ActiveMarker = styled.div(
  ({ theme }) => css`
    position: absolute;
    right: 0;
    top: 0;
    background-color: ${theme.co.primary20};
    width: ${theme.sz.s2};
    height: 100%;
    z-index: ${theme.zi.z9};
    border-radius: ${theme.sz.s2} 0 0 ${theme.sz.s2};
  `,
);

export const SidebarLink = styled.a<{ $active?: boolean }>(
  ({ theme, $active }) => css`
    width: 100%;
    height: ${theme.sz.s16};
    line-height: ${theme.sz.s8};
    display: flex;
    cursor: pointer;
    background-color: inherit;
    padding: ${theme.sz.s4} ${theme.sz.s4};
    border: none;
    color: ${theme.co.secondary20};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    font-size: ${theme.DEPRECATED_ty.large};
    font-weight: ${theme.DEPRECATED_ty.bold};
    text-decoration: none;

    &:hover {
      background: ${theme.co.neutral98};
    }

    @media (max-width: ${theme.sz.maxMobileBreakpoint}) {
      border-bottom: ${theme.sz.s2} solid ${$active ? theme.co.primary20 : 'transparent'};
      border-left: none;
      width: 50%;
      justify-content: center;
      margin-left: 0;
    }
  `,
);

export const MobileSidebarLink = styled(SidebarLink)<{
  $active?: boolean;
}>(
  ({ theme, $active }) => css`
    background-color: ${$active ? theme.co.neutral98 : theme.co.neutral99};
    padding: ${theme.sz.s1} 0 0 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
);

export const Icon = styled(IconComponent)<{ $collapsed?: boolean }>(
  ({ theme, $collapsed }) => css`
    position: relative;
    display: block;
    font-size: ${theme.sz.s8};
    line-height: ${theme.sz.s6};
    margin-right: ${$collapsed ? '' : theme.sz.s2};
  `,
);

export const IconMobile = styled(Icon)(
  ({ theme }) => css`
    margin: 0;
    font-size: ${theme.sz.s8};
  `,
);

export const TextMobile = styled.p(
  ({ theme }) => css`
    padding: 0;
    margin: 0;
    font-size: ${theme.DEPRECATED_ty.smallest};
    line-height: ${theme.sz.s5};
    font-weight: ${theme.DEPRECATED_ty.mediumWeight};
  `,
);

export const CollapseIcon = styled(IconComponent)(
  ({ theme }) => css`
    position: relative;
    display: flex;
    font-size: ${theme.sz.s3};
    line-height: ${theme.sz.s4};

    transform: scaleX(${rtlFlip(theme)});
  `,
);

export const SidebarCollapseButton = styled.button<{
  $collapsed?: boolean;
}>(
  ({ theme, $collapsed }) => css`
    position: absolute;
    left: ${$collapsed
      ? `calc(${theme.sz.s28} - ${theme.sz.s3})`
      : `calc(${theme.sz.s64} - ${theme.sz.s3})`};
    top: 70vh;
    z-index: ${theme.zi.z7};
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    background-color: ${theme.co.neutral99};
    border-radius: 50%;
    border: ${theme.bo.mediumWidth} solid ${theme.co.secondary80};
    color: ${theme.co.secondary80};
    cursor: pointer;

    @media (max-width: ${theme.sz.maxMobileBreakpoint}) {
      display: none;
    }
  `,
);

export const MobileNavBarWrapper = styled.div(
  ({ theme }) => css`
    border-top: ${theme.bo.mediumWidth} solid ${theme.co.neutral95};
    background-color: ${theme.co.neutral99};
    display: inline-flex;
    flex-direction: row;
    justify-content: space-evenly;
    width: 100%;
    height: 100%;

    @media (min-width: ${theme.sz.minMobileBreakpoint}) {
      display: none;
    }
  `,
);

export const ArrowCTAWrapper = styled.div(
  ({ theme }) => css`
    position: absolute;
    bottom: 0;
    border-top: ${theme.bo.thinWidth} solid ${theme.co.neutral90};
    text-align: center;
    width: 100%;
    height: ${theme.sz.s20};
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);

export const Separator = styled.div(
  ({ theme }) => css`
    width: 'calc(100% - ${theme.sz.s3})';
    border-bottom: ${theme.co.neutral95} solid ${theme.bo.thinWidth};
    margin: ${theme.sz.s4} 0 ${theme.sz.s4} ${theme.sz.s3};
  `,
);

export const LoaderContainer = styled.div(
  ({ theme }) => css`
    margin-left: ${theme.sz.s4};
    width: ${theme.sz.s10};
  `,
);
